import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import VisibilityPicker from "./VisibilityPicker";

/**
 * Iter-96 PART 3 — edit a single existing supervisor note.
 *
 * Lets the owner correct text, scene link, character link or label
 * after creation. Empty string in scene_id/character_id means "clear
 * the link". The parent supplies `scenes` and `characters` for the
 * dropdowns.
 */
export default function EditSupervisorNoteDialog({
    open,
    onOpenChange,
    note,
    scenes = [],
    characters = [],
    onSaved,
}) {
    const [text, setText] = useState("");
    const [sceneId, setSceneId] = useState("");
    const [characterId, setCharacterId] = useState("");
    const [label, setLabel] = useState("");
    // Iter-156B Phase 2 — visibility scope.
    const [visibility, setVisibility] = useState("everyone");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !note) return;
        setText(note.text || "");
        setSceneId(note.scene_id || "");
        setCharacterId(note.character_id || "");
        setLabel(note.label || "");
        setVisibility(note.visibility || "everyone");
        setError("");
    }, [open, note]);

    const submit = async () => {
        if (!text.trim()) {
            setError("Note text cannot be empty.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const payload = {
                text: text.trim(),
                scene_id: sceneId,
                character_id: characterId,
                label: label,
                visibility,
            };
            await onSaved(payload);
            onOpenChange(false);
        } catch (e) {
            setError(e?.response?.data?.detail || "Save failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
            <DialogContent
                data-testid="edit-supervisor-note-dialog"
                className="max-w-md bg-card border-violet-500/30"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-lg font-bold text-white">
                        Edit note
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Correct any field. Notes are reference only.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 mt-2">
                    <div>
                        <Label
                            htmlFor="edit-note-text"
                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Note
                        </Label>
                        <Textarea
                            id="edit-note-text"
                            data-testid="edit-supervisor-note-text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={4}
                            maxLength={4000}
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-y"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label
                                htmlFor="edit-note-scene"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Scene
                            </Label>
                            <select
                                id="edit-note-scene"
                                data-testid="edit-supervisor-note-scene"
                                value={sceneId}
                                onChange={(e) => setSceneId(e.target.value)}
                                className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none"
                            >
                                <option value="">— None —</option>
                                {scenes.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        Sc {s.scene_number || "?"}
                                        {s.heading ? ` · ${s.heading}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label
                                htmlFor="edit-note-char"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Character
                            </Label>
                            <select
                                id="edit-note-char"
                                data-testid="edit-supervisor-note-character"
                                value={characterId}
                                onChange={(e) => setCharacterId(e.target.value)}
                                className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none"
                            >
                                <option value="">— None —</option>
                                {characters.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label
                            htmlFor="edit-note-label"
                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Category / source
                        </Label>
                        <Input
                            id="edit-note-label"
                            data-testid="edit-supervisor-note-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Day 12, Director note"
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    </div>

                    {/* Iter-156B Phase 2 — visibility picker. */}
                    <VisibilityPicker
                        value={visibility}
                        onChange={setVisibility}
                        testidPrefix="edit-supervisor-note-visibility"
                    />

                    {error ? (
                        <p
                            data-testid="edit-supervisor-note-error"
                            className="text-xs text-red-400"
                        >
                            {error}
                        </p>
                    ) : null}

                    <div className="flex gap-2 pt-2">
                        <Button
                            type="button"
                            data-testid="edit-supervisor-note-cancel"
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            disabled={busy}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="edit-supervisor-note-save"
                            onClick={submit}
                            disabled={busy}
                            className="flex-1 min-tap"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
